import Loading from '@components/Loading';
import { FC, Fragment, ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import * as styles from './styles';

interface CanvasImageProps {
  url: string;
  defaultImage?: ReactNode;
}

const CanvasImage: FC<CanvasImageProps> = ({ url, defaultImage }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  // calculate canvas size
  const getCanvasSize = useCallback(
    (container: HTMLDivElement, image: HTMLImageElement) => {
      const containerWidth: number = container.clientWidth;
      const containerHeight: number = container.clientHeight;

      const imageWidth: number = image.naturalWidth;
      const imageHeight: number = image.naturalHeight;

      const containerRatio: number = containerWidth / containerHeight;
      const imageRatio: number = imageWidth / imageHeight;

      if (containerRatio > imageRatio) {
        // landscape image
        return [imageRatio * containerHeight, containerHeight];
      } else {
        // portrait image
        return [containerWidth, containerWidth / imageRatio];
      }
    },
    []
  );

  // `img` element load event (image loaded)
  const onLoad = useCallback(() => {
    if (containerRef.current && canvasRef.current && imgRef.current) {
      setIsLoaded(true);

      const [canvasWidth, canvasHeight] = getCanvasSize(
        containerRef.current,
        imgRef.current
      );

      // need to set attribute for the canvas context size
      canvasRef.current.setAttribute('width', `${canvasWidth}`);
      canvasRef.current.setAttribute('height', `${canvasHeight}`);

      const context = canvasRef.current.getContext('2d');
      if (context) {
        context.drawImage(
          imgRef.current,
          0,
          0,
          imgRef.current.naturalWidth,
          imgRef.current.naturalHeight,
          0,
          0,
          canvasWidth,
          canvasHeight
        );
      }
    }
  }, [getCanvasSize]);

  // Loader control
  useEffect(() => {
    setIsLoaded(false);
  }, [url]);

  const [loaderVisible, setLoaderVisible] = useState(true);

  useEffect(() => {
    setLoaderVisible(!isLoaded);
  }, [isLoaded]);

  return (
    <div ref={containerRef} css={styles.container}>
      <canvas ref={canvasRef} css={!isLoaded && styles.hide}></canvas>
      {!isLoaded && (
        <Fragment>
          <img ref={imgRef} src={url} alt={''} css={styles.hide} onLoad={onLoad} />
          {defaultImage}
        </Fragment>
      )}
      <Loading visible={loaderVisible} />
    </div>
  );
};

export default CanvasImage;
